/* ─────────────────────────────────────────────────────────────────────────────
 *  fuzzyMatch.ts — Fuzzy search utility using Levenshtein distance
 * ───────────────────────────────────────────────────────────────────────────── */

export interface FuzzyResult {
  candidate: string;
  distance: number;
  url: string;
}

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

export interface FuzzyCandidate {
  label: string;
  url: string;
}

export function fuzzySearch(
  query: string,
  candidates: FuzzyCandidate[]
): FuzzyResult[] {
  const q = query.toLowerCase().trim();
  if (!q) return [];

  const threshold = Math.max(3, Math.ceil(q.length * 0.4));

  return candidates
    .map((c) => ({
      candidate: c.label,
      distance: levenshtein(q, c.label.toLowerCase()),
      url: c.url,
    }))
    .filter((r) => r.distance <= threshold && r.distance > 0)
    .sort((a, b) => a.distance - b.distance);
}
